import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { WAR_ROOM_RULE_MAP, type WarRoomSignal } from '@goyone/shared';
import { api, qs } from '../lib/api';
import { plural, WORDS } from '../lib/format';
import { useSession } from '../auth/AuthProvider';
import { Icon } from './Icon';

/**
 * موجز اليوم.
 *
 * لا يحسب شيئًا جديدًا: يركّب ما تحسبه المسارات القائمة — إشارات غرفة
 * العمليات مقصورةً على المستخدم، ودلاء الأنشطة، واجتماعات اليوم. بناء محرّك
 * إشارات ثانٍ كان سيعني قاعدتين للإهمال تفترقان عند أول تعديل، فيقول الموجز
 * «ثلاثة عملاء مهملون» وتقول غرفة العمليات أربعة.
 *
 * وكل سطر فيه رابط: الموجز يُقرأ ليُعمَل به لا ليُقرأ. رقمٌ بلا طريق إليه
 * يضيف خطوة بدل أن يوفّرها.
 *
 * وما ليس في النظام ليس هنا: لا «هدف اليوم» ولا نسبة إنجاز مقارنةً به —
 * لا نموذج للأهداف في قاعدة البيانات، ورقمٌ مخترع يُقاس عليه الأداء أسوأ
 * من غياب الرقم.
 */

interface RuleGroup {
  rule: string;
  label: string;
  total: number;
  signals: WarRoomSignal[];
}

/**
 * شكل ردّ غرفة العمليات.
 *
 * كائن لا مصفوفة — وهذا ما أخطأتُ فيه أول مرة: كُتب `api<RuleGroup[]>` ثم
 * نودي `.flatMap` على كائن، فرُمي الخطأ أثناء التصيير وأسقط الشجرة كلها.
 *
 * و`api<T>` **توكيد** لا تحقّق: المعامل النوعي يصف ما نظنّه لا ما يصل، فلا
 * يمسك فحصُ الأنواع اختلافًا كهذا. أي شكل يأتي من الخادم يُقرأ من المصدر —
 * `war-room.service.ts → board()` — لا من الذاكرة.
 */
interface WarRoomBoard {
  groups: RuleGroup[];
  summary: { total: number; critical: number; amount: number; dismissed: number };
}

interface MeetingRow {
  id: string;
  subject: string;
  dueAt: string | null;
  account: { id: string; name: string } | null;
  lead: { id: string; name: string } | null;
}

/** تحية بحسب الساعة. الصباح والمساء ليسا سواءً لمن يقرأ. */
function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'صباح الخير';
  if (h < 17) return 'طاب يومك';
  return 'مساء الخير';
}

function hhmm(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
}

export function Briefing() {
  const session = useSession();

  const signals = useQuery({
    queryKey: ['war-room', 'briefing'],
    queryFn: () => api<WarRoomBoard>('/crm/war-room?mine=true&perRule=3'),
    staleTime: 60_000,
  });

  const stats = useQuery({
    queryKey: ['activities', 'stats', 'briefing'],
    queryFn: () => api<Record<string, number>>('/crm/activities/stats'),
    staleTime: 60_000,
  });

  const meetings = useQuery({
    queryKey: ['activities', 'briefing-meetings'],
    queryFn: () =>
      api<{ items: MeetingRow[] }>(
        `/crm/activities${qs({ bucket: 'today', type: 'MEETING', pageSize: 5, sort: 'dueAt', order: 'asc' })}`,
      ),
    staleTime: 60_000,
  });

  const overdue = stats.data?.overdue ?? 0;
  const today = stats.data?.today ?? 0;
  const doneRecently = stats.data?.done ?? 0;
  const todayMeetings = meetings.data?.items ?? [];

  /*
   * الإشارات مرتَّبة بما رتّبته به غرفة العمليات، ويؤخذ منها ثلاث.
   *
   * الموجز ليس القائمة الكاملة: عشرون سطرًا في صدر الشاشة تُتخطّى بالعين
   * كلها. ثلاثة تُقرأ، والرابط إلى غرفة العمليات لمن أراد الباقي.
   */
  const groups = signals.data?.groups ?? [];
  const top = groups.flatMap((g) => g.signals).slice(0, 3);
  const totalSignals = signals.data?.summary.total ?? 0;

  const loading = signals.isPending || stats.isPending;

  /* لا شيء يستحق العرض: لا موجز فارغ يشغل صدر الشاشة. */
  if (!loading && overdue === 0 && todayMeetings.length === 0 && top.length === 0) {
    return null;
  }

  const firstName = session.user.name.trim().split(/\s+/)[0] ?? '';

  return (
    <section className="briefing reveal" aria-label="موجز اليوم">
      <div className="briefing-head">
        <span className="briefing-icon">
          <Icon name="zap" size={18} />
        </span>
        <div>
          <h2 className="briefing-title">
            {greeting()}
            {firstName && ` يا ${firstName}`}
          </h2>
          <p className="briefing-sub">
            {loading ? (
              'جارٍ قراءة يومك…'
            ) : (
              <Summary
                overdue={overdue}
                today={today}
                meetings={todayMeetings.length}
                done={doneRecently}
              />
            )}
          </p>
        </div>
      </div>

      {loading && <div className="skeleton" style={{ height: 60 }} />}

      {!loading && (
        <div className="briefing-body">
          {todayMeetings.length > 0 && (
            <div className="briefing-block">
              <h3 className="briefing-block-title">اجتماعات اليوم</h3>
              <ul className="briefing-list">
                {todayMeetings.map((m) => (
                  <li key={m.id}>
                    <span className="num briefing-time">{hhmm(m.dueAt)}</span>
                    <span className="briefing-text">{m.subject}</span>
                    {m.account && (
                      <Link to={`/crm/accounts/${m.account.id}`} className="tiny">
                        {m.account.name}
                      </Link>
                    )}
                    {!m.account && m.lead && (
                      <Link to={`/crm/leads/${m.lead.id}`} className="tiny">
                        {m.lead.name}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {top.length > 0 && (
            <div className="briefing-block">
              <h3 className="briefing-block-title">يستحق انتباهك</h3>
              <ul className="briefing-list">
                {top.map((s) => (
                  <li key={s.key}>
                    <span className={`briefing-pri briefing-pri-${s.priority.toLowerCase()}`} />
                    <span className="briefing-text">
                      <SignalLink signal={s} />
                    </span>
                    <span className="tiny mute">{s.reason}</span>
                  </li>
                ))}
              </ul>

              {totalSignals > top.length && (
                <Link to="/crm/war-room" className="tiny">
                  وأخرى — افتح غرفة العمليات ({totalSignals})
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * السطر الذي يُقرأ أولًا.
 *
 * جملة واحدة تُبنى ممّا وُجد فعلًا: ذكرُ ما عدده صفر يجعل السطر يقول
 * «لديك صفر مهمة متأخرة» — وهي معلومة لا أحد يحتاجها في صدر شاشته.
 */
function Summary({
  overdue,
  today,
  meetings,
  done,
}: {
  overdue: number;
  today: number;
  meetings: number;
  done: number;
}) {
  const parts: string[] = [];

  if (overdue > 0) parts.push(`${plural(overdue, WORDS.task)} فات موعدها`);
  if (today > 0) parts.push(`${plural(today, WORDS.task)} اليوم`);
  if (meetings > 0) {
    parts.push(meetings === 1 ? 'واجتماع واحد' : `و${meetings} اجتماعات`);
  }

  if (!parts.length) {
    return <>لا شيء عاجل اليوم. {done > 0 && `أنجزت ${done} خلال الشهر الماضي.`}</>;
  }

  return <>{parts.join('، ')}.</>;
}

/** رابط الإشارة إلى سجلها. */
function SignalLink({ signal }: { signal: WarRoomSignal }) {
  const to = signal.leadId
    ? `/crm/leads/${signal.leadId}`
    : signal.accountId
      ? `/crm/accounts/${signal.accountId}`
      : signal.dealId
        ? `/sales/deals`
        : null;

  const label = WAR_ROOM_RULE_MAP[signal.rule]?.label ?? signal.rule;

  return to ? (
    <Link to={to} title={label}>
      {signal.title}
    </Link>
  ) : (
    <span title={label}>{signal.title}</span>
  );
}
